/**
 * Painel Berytus › Compliance (fase 0): regras com modo (off | observe | enforce) trocado sem deploy, eventos do que as
 * regras fariam, o disjuntor de ações em massa (aprovar, recusar, reverter lote), liberações provisórias e a tabela de
 * ICMS por UF e Áreas de Livre Comércio usada no PMC oficial. Mudanças: só superadmin.
 */
import { ChannelPolicies, ControlledReleases, DocumentsImpact, PrescriptionRetention } from './AdminCompliancePharma.tsx';
import { LockMedicineImpact } from './AdminContests.tsx';
import { useState } from 'react';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { AdminTenantRow } from '@berytus/contracts';
import { api, dateTime, int } from '../api.ts';
import { ErrorBox, Loading, Tag } from '../components/ui.tsx';
import { useMe } from '../components/AuthGate.tsx';
import { useUrlState } from './MeliChannel.tsx';

type Mode = 'off' | 'observe' | 'enforce';
interface Rule { rule: string; mode: Mode; description: string; enforceable: boolean; updatedAt: string | null; updatedBy: string | null; open: number; resolved: number; tenants: number }
interface Overview {
  rules: Rule[]; settings: Record<string, number>; masterItems: number;
  icms: { uf: string; aliquota: number; conferido: boolean }[]; alc: { uf: string; city: string }[]; pendingMassActions: number;
}
interface Event { id: string; tenant: string; rule: string; entity: string; action: string; reason: string; mode: string; occurrences: number; createdAt: string; lastSeenAt: string; resolvedAt: string | null }
interface MassAction { batchId: string; kind: string; rule: string | null; source: string; reason: string; status: string; tenants: string[]; items: number; requiredApprovals: number; approvals: number; createdAt: string; executedAt: string | null; revertedAt: string | null }
interface WaiverRow { id: string; tenantId: string; tenant: string; scope: string; reason: string; startsAt: string; endsAt: string; renewals: number; acceptedAt: string | null; acceptedBy: string | null; revokedAt: string | null; active: boolean }

const MODE: Record<Mode, { label: string; tone: string }> = {
  off: { label: 'Desligada', tone: 'off' }, observe: { label: 'Observando', tone: 'info' }, enforce: { label: 'Aplicando', tone: 'warn' },
};
const STATUS: Record<string, { label: string; tone: string }> = {
  executed: { label: 'Executado', tone: 'ok' }, pending_approval: { label: 'Aguardando aprovação', tone: 'warn' },
  rejected: { label: 'Recusado', tone: 'off' }, reverted: { label: 'Revertido', tone: 'info' },
};
const TABS = [['regras', 'Regras'], ['eventos', 'Eventos'], ['canais', 'Canais'], ['controlados', 'Liberações de controlados'], ['regularizacao', 'Regularização pendente'], ['retencao', 'Retenção de receitas'],
  ['disjuntor', 'Ações em massa'], ['liberacoes', 'Liberações provisórias'], ['icms', 'ICMS e ALC']] as const;
/** "dd/mm/aaaa hh:mm" (horário de Brasília) → Date; inválida: null. */
export function parseBrDateTime(s: string): Date | null {
  const m = /^\s*(\d{2})\/(\d{2})\/(\d{4})(?:\s+(\d{2}):(\d{2}))?\s*$/.exec(s);
  if (!m) return null;
  const d = new Date(`${m[3]}-${m[2]}-${m[1]}T${m[4] ?? '23'}:${m[5] ?? '59'}:00-03:00`);
  return Number.isNaN(d.getTime()) || d.getUTCDate() !== new Date(`${m[3]}-${m[2]}-${m[1]}T12:00:00-03:00`).getUTCDate() ? null : d;
}

const SETTINGS: [string, string][] = [
  ['mass_max_tenants', 'Contas afetadas (acima: aprovação)'], ['mass_max_items_pct', '% das fichas do mestre (acima: aprovação)'],
  ['mass_second_tenants', 'Contas afetadas (acima: 2 admins)'], ['mass_second_items_pct', '% das fichas do mestre (acima: 2 admins)'],
];

export function AdminCompliance() {
  const [params, set] = useUrlState();
  const tab = params.get('aba') ?? 'regras';
  const isSuper = useMe().user.platformRole === 'superadmin';
  const q = useQuery({ queryKey: ['admin', 'compliance'], queryFn: () => api<Overview>('/admin/compliance') });
  return (
    <>
      <div className="page-head">
        <div>
          <h1>Compliance</h1>
          <p>Regras de compliance com o modo de cada uma. Em <b>observando</b>, a regra só registra o que faria; nada é bloqueado, pausado ou alterado.
            Toda ação automática em massa passa pelo disjuntor.</p>
        </div>
      </div>
      <div className="stack-y">
        <LockMedicineImpact />
        <div className="seg" role="tablist" aria-label="Seção" style={{ justifySelf: 'start', flexWrap: 'wrap' }}>
          {TABS.map(([k, l]) => (
            <button key={k} type="button" aria-pressed={tab === k} onClick={() => set({ aba: k === 'regras' ? null : k, regra: null })}>
              {l}{k === 'disjuntor' && q.data?.pendingMassActions ? <> <Tag tone="warn">{q.data.pendingMassActions}</Tag></> : null}
            </button>
          ))}
        </div>
        {q.error ? <ErrorBox error={q.error} /> : !q.data ? <Loading /> : (
          tab === 'eventos' ? <Events rules={q.data.rules} rule={params.get('regra') ?? ''} onRule={(r) => set({ regra: r || null })} />
            : tab === 'disjuntor' ? <MassActions o={q.data} isSuper={isSuper} />
            : tab === 'liberacoes' ? <Waivers isSuper={isSuper} />
            : tab === 'canais' ? <ChannelPolicies isSuper={isSuper} />
            : tab === 'controlados' ? <ControlledReleases isSuper={isSuper} />
            : tab === 'regularizacao' ? <DocumentsImpact isSuper={isSuper} />
            : tab === 'retencao' ? <PrescriptionRetention isSuper={isSuper} />
            : tab === 'icms' ? <Icms o={q.data} isSuper={isSuper} />
            : <Rules o={q.data} isSuper={isSuper} onEvents={(r) => set({ aba: 'eventos', regra: r })} />
        )}
      </div>
    </>
  );
}

function useRefresh() {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: ['admin', 'compliance'] });
}

function Rules({ o, isSuper, onEvents }: { o: Overview; isSuper: boolean; onEvents: (rule: string) => void }) {
  const refresh = useRefresh();
  const save = useMutation({ mutationFn: (v: { rule: string; mode: Mode }) => api(`/admin/compliance/rules/${v.rule}`, { method: 'PUT', json: { mode: v.mode } }), onSuccess: refresh });
  return (
    <div className="panel flush">
      {save.error && <div style={{ padding: 12 }}><ErrorBox error={save.error} /></div>}
      <div className="grid-wrap">
        <table className="simple">
          <thead><tr><th>Regra</th><th>Modo</th><th className="num">Eventos abertos</th><th className="num">Contas</th><th>Alterada</th></tr></thead>
          <tbody>
            {o.rules.map((r) => (
              <tr key={r.rule}>
                <td style={{ maxWidth: 460, whiteSpace: 'normal' }}><b>{r.rule}</b><div className="small muted">{r.description}</div></td>
                <td>
                  {isSuper ? (
                    <select className="select" aria-label={`Modo de ${r.rule}`} value={r.mode} disabled={save.isPending}
                      onChange={(e) => {
                        const mode = e.target.value as Mode;
                        if (mode === 'enforce' && !window.confirm(`Ligar "${r.rule}" em APLICANDO? A regra passa a agir nos clientes (pelo disjuntor de ações em massa).`)) return;
                        save.mutate({ rule: r.rule, mode });
                      }}>
                      <option value="off">Desligada</option>
                      <option value="observe">Observando</option>
                      <option value="enforce" disabled={!r.enforceable}>Aplicando{r.enforceable ? '' : ' (fase seguinte)'}</option>
                    </select>
                  ) : <Tag tone={MODE[r.mode].tone}>{MODE[r.mode].label}</Tag>}
                </td>
                <td className="num">{r.open ? <button type="button" className="btn sm quiet" onClick={() => onEvents(r.rule)}>{int(r.open)}</button> : '0'}</td>
                <td className="num">{int(r.tenants)}</td>
                <td className="small muted">{r.updatedAt ? `${dateTime(r.updatedAt)}${r.updatedBy ? ` · ${r.updatedBy}` : ''}` : '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Events({ rules, rule, onRule }: { rules: Rule[]; rule: string; onRule: (r: string) => void }) {
  const [open, setOpen] = useState<'true' | 'false' | 'all'>('true');
  const q = useQuery({
    queryKey: ['admin', 'compliance', 'events', rule, open],
    queryFn: () => api<Event[]>(`/admin/compliance/events?open=${open}${rule ? `&rule=${rule}` : ''}`),
    placeholderData: keepPreviousData,
  });
  return (
    <div className="stack-y">
      <div className="toolbar">
        <select className="select" aria-label="Regra" value={rule} onChange={(e) => onRule(e.target.value)}>
          <option value="">Todas as regras</option>
          {rules.map((r) => <option key={r.rule} value={r.rule}>{r.rule} ({int(r.open)})</option>)}
        </select>
        <select className="select" aria-label="Situação" value={open} onChange={(e) => setOpen(e.target.value as typeof open)}>
          <option value="true">Abertos</option><option value="false">Resolvidos</option><option value="all">Todos</option>
        </select>
      </div>
      {q.error ? <ErrorBox error={q.error} /> : !q.data ? <Loading /> : !q.data.length ? <div className="panel empty"><b>Nenhum evento</b>Nada registrado com esse filtro.</div> : (
        <div className="panel flush" style={{ opacity: q.isPlaceholderData ? 0.6 : 1 }}>
          <div className="grid-wrap">
            <table className="simple">
              <thead><tr><th>Conta</th><th>Regra</th><th>Motivo</th><th>Faria</th><th className="num">Vezes</th><th>Visto por último</th></tr></thead>
              <tbody>
                {q.data.map((e) => (
                  <tr key={e.id}>
                    <td>{e.tenant}</td>
                    <td className="small">{e.rule}<div className="muted">{e.entity}</div></td>
                    <td style={{ maxWidth: 420, whiteSpace: 'normal' }}>{e.reason}</td>
                    <td><Tag tone={e.mode === 'enforce' ? 'warn' : 'info'}>{e.action}</Tag>{e.mode !== 'enforce' && <div className="small muted">não executado</div>}</td>
                    <td className="num">{int(e.occurrences)}</td>
                    <td className="small">{dateTime(e.lastSeenAt)}{e.resolvedAt && <div className="muted">resolvido {dateTime(e.resolvedAt)}</div>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

function MassActions({ o, isSuper }: { o: Overview; isSuper: boolean }) {
  const refresh = useRefresh();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['admin', 'compliance', 'mass'], queryFn: () => api<MassAction[]>('/admin/compliance/mass-actions') });
  const act = useMutation({
    mutationFn: (v: { id: string; what: 'approve' | 'reject' | 'revert' }) => api(`/admin/compliance/mass-actions/${v.id}/${v.what}`, { method: 'POST', json: {} }),
    onSuccess: () => { refresh(); qc.invalidateQueries({ queryKey: ['admin', 'compliance', 'mass'] }); },
  });
  const [limits, setLimits] = useState<Record<string, string>>(() => Object.fromEntries(SETTINGS.map(([k]) => [k, String(o.settings[k] ?? '')])));
  const saveLimits = useMutation({
    mutationFn: () => api('/admin/compliance/settings', { method: 'PUT', json: Object.fromEntries(Object.entries(limits).map(([k, v]) => [k, Number(v)])) }),
    onSuccess: refresh,
  });
  return (
    <div className="stack-y">
      <section className="panel stack-y" style={{ gap: 8 }}>
        <h2 style={{ margin: 0 }}>Limites do disjuntor</h2>
        <p className="small muted" style={{ margin: 0 }}>Ação automática (regra ou carga de fonte) acima de qualquer limite não é executada: fica aguardando aprovação aqui.
          Fichas no catálogo mestre: {int(o.masterItems)}.</p>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'end' }}>
          {SETTINGS.map(([k, l]) => (
            <label key={k} className="small" style={{ display: 'grid', gap: 4 }}>{l}
              <input className="input" type="number" min={0} step="any" style={{ width: 120 }} disabled={!isSuper} value={limits[k] ?? ''} onChange={(e) => setLimits({ ...limits, [k]: e.target.value })} />
            </label>
          ))}
          {isSuper && <button type="button" className="btn" disabled={saveLimits.isPending} onClick={() => saveLimits.mutate()}>Salvar limites</button>}
        </div>
        {saveLimits.error && <ErrorBox error={saveLimits.error} />}
      </section>
      {act.error && <ErrorBox error={act.error} />}
      {q.error ? <ErrorBox error={q.error} /> : !q.data ? <Loading /> : !q.data.length ? <div className="panel empty"><b>Nenhuma ação em massa</b>Nenhuma regra está aplicando nesta fase.</div> : (
        <div className="panel flush">
          <div className="grid-wrap">
            <table className="simple">
              <thead><tr><th>Lote</th><th>Situação</th><th>Contas</th><th className="num">Itens</th><th>Quando</th><th /></tr></thead>
              <tbody>
                {q.data.map((m) => (
                  <tr key={m.batchId}>
                    <td style={{ maxWidth: 360, whiteSpace: 'normal' }}><b>{m.kind}</b>{m.rule && <span className="small muted"> · {m.rule}</span>}<div className="small muted">{m.reason} · {m.source}</div></td>
                    <td><Tag tone={STATUS[m.status]?.tone ?? 'off'}>{STATUS[m.status]?.label ?? m.status}</Tag>
                      {m.status === 'pending_approval' && <div className="small muted">{m.approvals} de {m.requiredApprovals} aprovação(ões)</div>}</td>
                    <td className="small" style={{ maxWidth: 220, whiteSpace: 'normal' }}>{m.tenants.join(', ')}</td>
                    <td className="num">{int(m.items)}</td>
                    <td className="small">{dateTime(m.createdAt)}{m.revertedAt && <div className="muted">revertido {dateTime(m.revertedAt)}</div>}</td>
                    <td>
                      {isSuper && m.status === 'pending_approval' && (
                        <div style={{ display: 'flex', gap: 6 }}>
                          <button type="button" className="btn sm primary" disabled={act.isPending} onClick={() => { if (window.confirm('Aprovar e executar este lote?')) act.mutate({ id: m.batchId, what: 'approve' }); }}>Aprovar</button>
                          <button type="button" className="btn sm" disabled={act.isPending} onClick={() => act.mutate({ id: m.batchId, what: 'reject' })}>Recusar</button>
                        </div>
                      )}
                      {isSuper && m.status === 'executed' && (
                        <button type="button" className="btn sm" disabled={act.isPending}
                          onClick={() => { if (window.confirm('Reverter o lote? Restaura o preço anterior, reenvia aos canais e volta a regra para "observando".')) act.mutate({ id: m.batchId, what: 'revert' }); }}>Reverter</button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

function Waivers({ isSuper }: { isSuper: boolean }) {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['admin', 'compliance', 'waivers'], queryFn: () => api<WaiverRow[]>('/admin/compliance/waivers') });
  const tenants = useQuery({ queryKey: ['admin', 'tenants', 'all'], queryFn: () => api<AdminTenantRow[]>('/admin/tenants'), enabled: isSuper });
  const done = () => qc.invalidateQueries({ queryKey: ['admin', 'compliance', 'waivers'] });
  const [form, setForm] = useState({ tenantId: '', reason: '', endsAt: '' });
  const endsAt = parseBrDateTime(form.endsAt);
  const grant = useMutation({
    mutationFn: () => api('/admin/compliance/waivers', { method: 'POST', json: { ...form, endsAt: endsAt!.toISOString() } }),
    onSuccess: () => { setForm({ tenantId: '', reason: '', endsAt: '' }); done(); },
  });
  const act = useMutation({
    mutationFn: (v: { id: string; what: 'renew' | 'revoke'; endsAt?: string; reason?: string }) =>
      api(`/admin/compliance/waivers/${v.id}/${v.what}`, { method: 'POST', json: v.what === 'renew' ? { endsAt: v.endsAt, reason: v.reason } : {} }),
    onSuccess: done,
  });
  return (
    <div className="stack-y">
      <p className="small muted" style={{ margin: 0 }}>Liberação provisória (nível 1): até 7 dias, uma renovação de até 7 dias. Nunca cobre antimicrobianos nem
        Portaria 344. O cliente vê um aviso no painel enquanto ela vale e recebe e-mail 3 dias e 1 dia antes do fim.</p>
      {isSuper && (
        <section className="panel" style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'end' }}>
          <label className="small" style={{ display: 'grid', gap: 4 }}>Conta
            <select className="select" value={form.tenantId} onChange={(e) => setForm({ ...form, tenantId: e.target.value })}>
              <option value="">Escolha…</option>
              {(tenants.data ?? []).map((t) => <option key={t.id} value={t.id}>{t.name} ({t.vertical === 'pharmacy' ? 'farmácia' : 'geral'})</option>)}
            </select>
          </label>
          <label className="small" style={{ display: 'grid', gap: 4 }}>Termina em (dd/mm/aaaa hh:mm)
            <input className="input" inputMode="numeric" placeholder="dd/mm/aaaa hh:mm" value={form.endsAt} style={{ width: 170 }}
              aria-invalid={!!form.endsAt && !endsAt} onChange={(e) => setForm({ ...form, endsAt: e.target.value })} />
          </label>
          <label className="small" style={{ display: 'grid', gap: 4, flex: '1 1 260px' }}>Motivo (obrigatório)
            <input className="input" value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} placeholder="Ex.: documentos em análise" />
          </label>
          <button type="button" className="btn primary" disabled={!form.tenantId || !endsAt || form.reason.trim().length < 5 || grant.isPending} onClick={() => grant.mutate()}>Conceder</button>
          {grant.error && <div style={{ flexBasis: '100%' }}><ErrorBox error={grant.error} /></div>}
        </section>
      )}
      {act.error && <ErrorBox error={act.error} />}
      {q.error ? <ErrorBox error={q.error} /> : !q.data ? <Loading /> : !q.data.length ? <div className="panel empty"><b>Nenhuma liberação</b>Nenhuma conta com liberação provisória.</div> : (
        <div className="panel flush">
          <div className="grid-wrap">
            <table className="simple">
              <thead><tr><th>Conta</th><th>Situação</th><th>Período</th><th>Motivo</th><th>Aceite do cliente</th><th /></tr></thead>
              <tbody>
                {q.data.map((w) => (
                  <tr key={w.id}>
                    <td>{w.tenant}</td>
                    <td><Tag tone={w.active ? 'warn' : 'off'}>{w.revokedAt ? 'Revogada' : w.active ? 'Vigente' : 'Vencida'}</Tag>{w.renewals > 0 && <div className="small muted">renovada</div>}</td>
                    <td className="small">{dateTime(w.startsAt)} → {dateTime(w.endsAt)}</td>
                    <td style={{ maxWidth: 320, whiteSpace: 'normal' }} className="small">{w.reason}</td>
                    <td className="small">{w.acceptedAt ? <>{dateTime(w.acceptedAt)}{w.acceptedBy ? ` · ${w.acceptedBy}` : ''}</> : <span className="muted">pendente</span>}</td>
                    <td>
                      {isSuper && w.active && (
                        <div style={{ display: 'flex', gap: 6 }}>
                          {w.renewals === 0 && (
                            <button type="button" className="btn sm" disabled={act.isPending} onClick={() => {
                              const days = Number(window.prompt('Renovar por quantos dias (1 a 7)?', '7'));
                              if (!days || days < 1 || days > 7) return;
                              const reason = window.prompt('Motivo da renovação:') ?? '';
                              if (reason.trim().length < 5) return;
                              act.mutate({ id: w.id, what: 'renew', endsAt: new Date(new Date(w.endsAt).getTime() + days * 86_400_000).toISOString(), reason });
                            }}>Renovar</button>
                          )}
                          <button type="button" className="btn sm" disabled={act.isPending} onClick={() => { if (window.confirm('Revogar a liberação agora?')) act.mutate({ id: w.id, what: 'revoke' }); }}>Revogar</button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

function Icms({ o, isSuper }: { o: Overview; isSuper: boolean }) {
  const refresh = useRefresh();
  const save = useMutation({ mutationFn: (v: { uf: string; aliquota: number; conferido: boolean }) => api(`/admin/compliance/icms/${v.uf}`, { method: 'PUT', json: v }), onSuccess: refresh });
  const [alc, setAlc] = useState({ uf: '', city: '' });
  const addAlc = useMutation({ mutationFn: () => api('/admin/compliance/alc', { method: 'POST', json: alc }), onSuccess: () => { setAlc({ uf: '', city: '' }); refresh(); } });
  const delAlc = useMutation({ mutationFn: (v: { uf: string; city: string }) => api(`/admin/compliance/alc?uf=${v.uf}&city=${encodeURIComponent(v.city)}`, { method: 'DELETE' }), onSuccess: refresh });
  const pending = o.icms.filter((i) => !i.conferido).length;
  return (
    <div className="cols">
      <section className="panel stack-y" style={{ gap: 8 }}>
        <h2 style={{ margin: 0 }}>ICMS de medicamento por UF</h2>
        <p className="small muted" style={{ margin: 0 }}>Escolhe a coluna de PMC (CMED/ABCFarma) pela UF do endereço da conta.
          {pending > 0 && <> <b>{pending} UF(s) ainda não conferida(s)</b>: valores iniciais pela alíquota modal; confira antes de ligar o teto de PMC.</>}</p>
        {save.error && <ErrorBox error={save.error} />}
        <div className="grid-wrap">
          <table className="simple">
            <thead><tr><th>UF</th><th className="num">Alíquota (%)</th><th>Conferido</th></tr></thead>
            <tbody>
              {o.icms.map((i) => (
                <tr key={i.uf}>
                  <td>{i.uf}</td>
                  <td className="num">
                    {isSuper ? (
                      <input className="input" type="number" step="0.5" min={0} max={40} style={{ width: 90 }} defaultValue={i.aliquota}
                        onBlur={(e) => { const v = Number(e.target.value); if (v !== i.aliquota) save.mutate({ uf: i.uf, aliquota: v, conferido: true }); }} />
                    ) : i.aliquota}
                  </td>
                  <td>
                    {isSuper ? <input type="checkbox" className="check" aria-label={`${i.uf} conferido`} checked={i.conferido} onChange={(e) => save.mutate({ uf: i.uf, aliquota: i.aliquota, conferido: e.target.checked })} />
                      : i.conferido ? 'sim' : 'não'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
      <section className="panel stack-y" style={{ gap: 8 }}>
        <h2 style={{ margin: 0 }}>Áreas de Livre Comércio</h2>
        <p className="small muted" style={{ margin: 0 }}>Conta com endereço num destes municípios usa o PMC "ALC".</p>
        {(addAlc.error || delAlc.error) && <ErrorBox error={addAlc.error ?? delAlc.error} />}
        <ul className="small" style={{ margin: 0, paddingLeft: 18 }}>
          {o.alc.map((a) => (
            <li key={`${a.uf}-${a.city}`}>{a.city} ({a.uf})
              {isSuper && <button type="button" className="btn sm quiet" style={{ padding: '0 4px' }} onClick={() => delAlc.mutate(a)}>tirar</button>}</li>
          ))}
        </ul>
        {isSuper && (
          <div style={{ display: 'flex', gap: 6 }}>
            <input className="input" placeholder="UF" maxLength={2} style={{ width: 60 }} value={alc.uf} onChange={(e) => setAlc({ ...alc, uf: e.target.value.toUpperCase() })} />
            <input className="input" placeholder="Município" value={alc.city} onChange={(e) => setAlc({ ...alc, city: e.target.value })} />
            <button type="button" className="btn" disabled={alc.uf.length !== 2 || alc.city.trim().length < 2 || addAlc.isPending} onClick={() => addAlc.mutate()}>Incluir</button>
          </div>
        )}
      </section>
    </div>
  );
}
